import React from 'react';
import CloseRounded from '@mui/icons-material/CloseRounded';
import { Box, IconButton, Modal as MuiModal, Typography } from '@mui/material';

import Button from '../Button';
import { v2Colors } from '../../design/theme';
import {
  customizationAddOnPriceLabel,
  customizationOptionSubtext,
  isSkinlessSelected,
  isSmokeTurmericOption,
  isCheckboxCustomization,
  optionKey,
  sectionLabel,
} from './productCustomizationHelpers';

const sectionHeadingSx = {
  fontSize: '0.68rem',
  fontWeight: 600,
  letterSpacing: '0.1em',
  color: v2Colors.textMuted,
  mb: 1,
};

function RadioOptionCard({ title, subtext, selected, disabled, onClick }) {
  return (
    <Box
      component="button"
      type="button"
      disabled={disabled}
      onClick={onClick}
      sx={{
        flex: 1,
        minWidth: 0,
        p: 1.5,
        borderRadius: 2,
        border: '2px solid',
        borderColor: selected ? v2Colors.primary : v2Colors.borderMuted,
        bgcolor: selected ? v2Colors.selectedFill : v2Colors.white,
        textAlign: 'left',
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontFamily: 'inherit',
        transition: 'border-color 0.15s, background-color 0.15s',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: v2Colors.heading, lineHeight: 1.25 }}>
        {title}
      </Typography>
      {subtext ? (
        <Typography sx={{ fontSize: '0.75rem', color: v2Colors.textMuted, mt: 0.5, lineHeight: 1.3 }}>
          {subtext}
        </Typography>
      ) : null}
    </Box>
  );
}

function PreferenceRow({ label, priceLabel, checked, onChange }) {
  return (
    <Box
      component="label"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        p: 1.5,
        borderRadius: 2,
        bgcolor: v2Colors.surface,
        cursor: 'pointer',
        mb: 0.75,
      }}
    >
      <Box
        sx={{
          width: 22,
          height: 22,
          borderRadius: 0.75,
          border: '2px solid',
          borderColor: checked ? v2Colors.primary : v2Colors.borderMuted,
          bgcolor: checked ? v2Colors.primary : v2Colors.white,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {checked ? (
          <Typography component="span" sx={{ color: v2Colors.white, fontSize: 14, lineHeight: 1, fontWeight: 700 }}>
            ✓
          </Typography>
        ) : null}
      </Box>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
      />
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: v2Colors.heading, lineHeight: 1.35 }}>
          {label}
        </Typography>
        {priceLabel ? (
          <Typography
            sx={{
              fontSize: '0.85rem',
              fontWeight: 600,
              color: v2Colors.primary,
              flexShrink: 0,
            }}
          >
            {priceLabel}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}

/**
 * @param {{
 *   open: boolean,
 *   onClose: (event?: object, reason?: string) => void,
 *   title?: string,
 *   customizations: [string, object][],
 *   selectedCustomizations: object,
 *   onSelectSingle: (groupKey: string, opt: object) => void,
 *   onToggleCheckbox: (groupKey: string, opt: object, checked: boolean) => void,
 *   lineTotal: number,
 *   adding: boolean,
 *   onConfirm: () => void,
 * }} props
 */
export default function ProductCustomizationSheet({
  open,
  onClose,
  title = 'Customize your meat',
  customizations,
  selectedCustomizations,
  onSelectSingle,
  onToggleCheckbox,
  lineTotal,
  adding,
  onConfirm,
}) {
  const smokeTurmericBlocked = isSkinlessSelected(selectedCustomizations, customizations);

  return (
    <MuiModal open={open} onClose={onClose} sx={{ zIndex: (t) => t.zIndex.modal }}>
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          mx: 'auto',
          maxWidth: 520,
          maxHeight: 'min(92vh, 720px)',
          bgcolor: v2Colors.white,
          borderRadius: '24px 24px 0 0',
          boxShadow: '0 -8px 32px rgba(0,0,0,0.12)',
          display: 'flex',
          flexDirection: 'column',
          outline: 0,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 4,
            borderRadius: 2,
            bgcolor: v2Colors.borderMuted,
            mx: 'auto',
            mt: 1.25,
            flexShrink: 0,
          }}
        />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 1,
            px: 2,
            pt: 1.5,
            pb: 1,
            flexShrink: 0,
          }}
        >
          <Typography
            component="h2"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1.35rem', sm: '1.5rem' },
              color: v2Colors.heading,
              lineHeight: 1.2,
              pr: 1,
            }}
          >
            {title}
          </Typography>
          <IconButton
            aria-label="Close"
            onClick={onClose}
            sx={{
              width: 36,
              height: 36,
              bgcolor: v2Colors.surface,
              color: v2Colors.heading,
              '&:hover': { bgcolor: v2Colors.selectedFill },
            }}
          >
            <CloseRounded fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ flex: 1, overflow: 'auto', px: 2, pb: 2 }}>
          {customizations.map(([key, customization]) => {
            const checkbox = isCheckboxCustomization(customization, key);
            const options = customization?.options ?? [];

            if (checkbox) {
              const selectedSet = new Set(
                Array.isArray(selectedCustomizations[key]) ? selectedCustomizations[key] : []
              );
              return (
                <Box key={key} sx={{ mb: 2 }}>
                  <Typography sx={sectionHeadingSx}>
                    {sectionLabel(customization.displayName, key)}
                  </Typography>
                  {options.map((opt) => {
                    const oid = optionKey(opt);
                    return (
                      <PreferenceRow
                        key={oid || opt.name}
                        label={opt.name}
                        priceLabel={customizationAddOnPriceLabel(opt)}
                        checked={selectedSet.has(oid)}
                        onChange={(checked) => onToggleCheckbox(key, opt, checked)}
                      />
                    );
                  })}
                </Box>
              );
            }

            return (
              <Box key={key} sx={{ mb: 2 }}>
                <Typography sx={sectionHeadingSx}>
                  {sectionLabel(customization.displayName, key)}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {options.map((opt) => {
                    const oid = optionKey(opt);
                    const active = selectedCustomizations[key] === oid;
                    const disabled =
                      key === 'flavour' && isSmokeTurmericOption(opt) && smokeTurmericBlocked;
                    return (
                      <RadioOptionCard
                        key={oid || opt.name}
                        title={opt.name}
                        subtext={
                          disabled
                            ? 'Not available with Skinless'
                            : customizationOptionSubtext(key, opt)
                        }
                        selected={active}
                        disabled={disabled}
                        onClick={() => !disabled && onSelectSingle(key, opt)}
                      />
                    );
                  })}
                </Box>
              </Box>
            );
          })}
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            px: 2,
            py: 2,
            borderTop: `1px solid ${v2Colors.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            pb: 'max(16px, env(safe-area-inset-bottom))',
          }}
        >
          <Box>
            <Typography sx={{ ...sectionHeadingSx, mb: 0.25 }}>Subtotal</Typography>
            <Typography sx={{ fontWeight: 700, fontSize: '1.75rem', color: v2Colors.heading, lineHeight: 1.1 }}>
              ₹{lineTotal}
            </Typography>
          </Box>
          <Button
            disabled={adding}
            onClick={onConfirm}
            sx={{
              flexShrink: 0,
              py: 1.5,
              px: 2.5,
              borderRadius: 9999,
              fontWeight: 700,
              fontSize: '0.95rem',
              textTransform: 'none',
              color: v2Colors.white,
              bgcolor: v2Colors.primary,
              boxShadow: '0 4px 14px rgba(165, 42, 60, 0.28)',
              '&:hover': { bgcolor: v2Colors.primaryDark },
            }}
          >
            {adding ? 'Adding…' : 'Add to bag →'}
          </Button>
        </Box>
      </Box>
    </MuiModal>
  );
}
